// 사람 이름 안전 판정 한 벌 — 가입 이름 검사(onboarding/rules)와 메일 본문 방어(mail/templates)가 같이 쓴다.

// 폭 없는 공백·BOM·보이지 않는 결합 글자(CGJ·크메르 모음 자리·몽골 자형 선택자) — 복사 붙여넣기로 섞여 들어오므로 거부하지 않고 지운다.
const INVISIBLE_ALL = /[\u200B\uFEFF\u034F\u17B4\u17B5\u180B-\u180D\u180F]/g;
// 공백처럼 보이는 글자(모든 Zs 공백·한글 채움·점자 빈칸) — 일반 공백으로 바꾼다.
const BLANK_LIKE_ALL = /[\p{Zs}\u3164\u115F\u1160\uFFA0\u2800]/gu;
// 이모지 블록(U+1F000~1FAFF)의 미할당 글자 — 런타임 유니코드가 낡아도 새 이모지로 본다(이체자 선택자·ZWJ 이웃 판정).
const NEW_EMOJI = String.raw`(?=[\u{1F000}-\u{1FAFF}])\p{Cn}`;
// 이모지가 아닌 글자 뒤의 이체자 선택자 — 글자를 쪼개는 데만 쓰이므로 지운다(하트 이모지·키캡 숫자·새 이모지 뒤 VS16 은 남는다).
const STRAY_VS_ALL = new RegExp(String.raw`(?<!\p{Emoji}|${NEW_EMOJI})[\uFE00-\uFE0F\u{E0100}-\u{E01EF}]`, "gu");
// virama — ZWJ·ZWNJ 를 남기는 인도계·싱할라 10개 문자권 것만(말라얄람 세로선·원형 virama 포함) · 미얀마·크메르·티베트·타이 등은 자모 겹침을 별도 글자(미얀마 1039·크메르 17D2·티베트 아래 글자)로 적어 ZWJ·ZWNJ 가 모양을 정하지 않으므로 넣지 않는다.
const VIRAMA = String.raw`\u094D\u09CD\u0A4D\u0ACD\u0B4D\u0BCD\u0C4D\u0CCD\u0D3B\u0D3C\u0D4D\u0DCA`;
// ZWJ 이웃으로 보는 이모지 — \p{Emoji} 에서 키캡 재료인 ASCII 숫자·#·* 는 빼고 새 이모지는 넣는다.
const EMOJI = String.raw`(?:[^\P{Emoji}#*0-9]|${NEW_EMOJI})`;
// ZWJ·ZWNJ 가 모양을 정하는 문자권 — 인도계·싱할라는 virama 에 붙은 것만 · 잇는 글자(아랍·시리아·응코·타나)는 글자 사이면 남긴다(아랍 글자 사이 ZWJ 는 대개 보이는 변화가 없지만 감수 — 가르려면 글자별 잇기 표가 필요하다).
const VIRAMA_SCRIPTS = ["Devanagari", "Bengali", "Gurmukhi", "Gujarati", "Oriya", "Tamil", "Telugu", "Kannada", "Malayalam", "Sinhala"];
const JOINING_SCRIPTS = ["Arabic", "Syriac", "Nko", "Thaana"];
// 한 문자권 안의 ZWJ·ZWNJ — 앞뒤 기준 글자(그 문자권 결합 부호만 건너뛴다)가 둘 다 그 문자권 글자 · virama 문자권은 조인어가 virama 바로 뒤나 바로 앞일 때만.
const scriptJoiner = (script: string, virama: boolean): string => {
  const letter = String.raw`(?=\p{L})\p{sc=${script}}`;
  const marks = String.raw`(?:(?=\p{M})\p{scx=${script}})*`;
  const near = virama ? String.raw`(?:(?<=[${VIRAMA}].)|(?=[${VIRAMA}]))` : "";
  return String.raw`(?<=${letter}${marks}.)${near}(?=${marks}${letter})`;
};
// 말라얄람 칠루 — 글자+virama+ZWJ 뒤가 문자열 끝·공백일 때만 남긴다(숫자·문장부호·이모지·다른 문자권이 붙으면 지운다).
const CHILLU = String.raw`(?<=(?=\p{L})\p{sc=Malayalam}(?:(?=\p{M})\p{scx=Malayalam})*\u0D4D\u200D)(?![^ ])`;
// 이모지 사이 ZWJ — 앞은 이모지·피부색·VS16·태그 끝, 뒤는 이모지.
const EMOJI_JOINER = String.raw`(?<=(?:${EMOJI}|[\uFE0F\u{E007F}])\u200D)(?=${EMOJI})`;
// 잇는 글자(아랍·시리아·응코·타나) 한 글자 · 그 문자권 결합 부호 한 글자 — 조인어 연속의 앞뒤를 한 글자씩 본다.
const JOINING_LETTER_ONE = new RegExp(String.raw`^(?=\p{L})(?:${JOINING_SCRIPTS.map((s) => String.raw`\p{sc=${s}}`).join("|")})$`, "u");
const JOINING_MARK_ONE = new RegExp(String.raw`^(?=\p{M})(?:${JOINING_SCRIPTS.map((s) => String.raw`\p{scx=${s}}`).join("|")})$`, "u");
// 이어 쓴 ZWJ·ZWNJ 연속 — 정규식 되짚기 없이 연속 하나씩 접는다(긴 연속도 입력 길이에 선형).
const JOINER_RUN_ALL = /[\u200C\u200D]{2,}/g;

// at 에서 step 방향으로 그 문자권 결합 부호를 건너뛴 첫 글자가 잇는 글자인가 — 서로게이트 쌍은 한 글자로 본다.
const joiningLetterAt = (text: string, at: number, step: 1 | -1): boolean => {
  let i = at;
  for (;;) {
    if (step > 0 ? i >= text.length : i <= 0) return false;
    const low = step < 0 && i >= 2 && /[\uDC00-\uDFFF]/.test(text[i - 1]) && /[\uD800-\uDBFF]/.test(text[i - 2]);
    const ch = step > 0 ? String.fromCodePoint(text.codePointAt(i) ?? 0) : text.slice(low ? i - 2 : i - 1, i);
    if (!JOINING_MARK_ONE.test(ch)) return JOINING_LETTER_ONE.test(ch);
    i += step * ch.length;
  }
};

// 연속 하나를 접는다 — 잇는 글자 사이에 ZWNJ 가 섞였으면 ZWNJ(페르시아어 끊기는 쓴 뜻) · 그 밖에 ZWJ 가 섞였으면 ZWJ(이모지 묶음·말라얄람 칠루·싱할라 스리·벵골 야팔라) · ZWNJ 뿐이면 ZWNJ.
const foldJoinerRun = (run: string, offset: number, text: string): string => {
  if (!run.includes("\u200D")) return "\u200C";
  if (!run.includes("\u200C")) return "\u200D";
  return joiningLetterAt(text, offset, -1) && joiningLetterAt(text, offset + run.length, 1) ? "\u200C" : "\u200D";
};

// ZWJ·ZWNJ 하나 — 남길 자리면 1번 묶음이 빈 문자열로 잡힌다(빈 대안 「|)」 — 「?」 는 빈 반복을 버려 묶음이 안 잡힌다 · 싱할라 스리·힌디 반자·벵골 야팔라·말라얄람 칠루·페르시아어 끊기·이모지 묶음) · 나머지는 지운다.
const JOINER_ALL = new RegExp(
  String.raw`[\u200C\u200D](?:(${[...VIRAMA_SCRIPTS.map((s) => scriptJoiner(s, true)), ...JOINING_SCRIPTS.map((s) => scriptJoiner(s, false)), CHILLU, EMOJI_JOINER].join("|")})|)`,
  "gu",
);
// 실제 지역 깃발(잉글랜드·스코틀랜드·웨일스) 태그 시퀀스는 통째로 잡아 살리고, 나머지 제어문자(C0/C1)·줄·문단 구분자·서식 문자(Cf — 방향 바꾸기·WJ·SHY·태그 등)·사용자 정의(Co)·짝 없는 서로게이트(Cs)·비문자·보이지 않는 미할당 글자는 거부한다 — ZWNJ·ZWJ 만 허용 · 미할당은 런타임 유니코드 버전에 따라 새 이모지가 막히지 않게 기본 무시 영역만 본다.
const FLAG_OR_CONTROL_ALL = /(\u{1F3F4}\u{E0067}\u{E0062}(?:\u{E0065}\u{E006E}\u{E0067}|\u{E0073}\u{E0063}\u{E0074}|\u{E0077}\u{E006C}\u{E0073})\u{E007F})|(?:(?![\u200C\u200D])[\p{Cc}\p{Cf}\p{Co}\p{Cs}\p{Noncharacter_Code_Point}\u2028\u2029]|(?=\p{Default_Ignorable_Code_Point})\p{Cn})+/gu;
// 지우고 나면 아무것도 안 보이는 글자 — 보이지 않는 글자·결합 부호·공백·점자 빈칸(U+2800).
const BLANK_ALL = /[\p{Default_Ignorable_Code_Point}\p{M}\s\u2800]/gu;
// 주소 판정 전에 지우는 것 — 보이지 않는 글자(ZWJ·CGJ·이체자 선택자·한글 채움 등)와 결합 부호로 점·TLD 를 쪼개는 우회를 막는다.
const IGNORABLE_ALL = /[\p{Default_Ignorable_Code_Point}\p{M}]/gu;
// 주소처럼 보이는 꼴 — 스킴·www·「두 글자.ASCII 2~24글자」(뒤에 영숫자가 없으면 · 한글이 붙어도 잡는다)·한국어 IDN TLD(뒤에 글자가 이어지면 이름으로 본다)·IPv4 — 반복 길이를 묶어 입력 길이에 선형이다.
const URL_LIKE = /:\/\/|www\.|[\p{L}\p{N}-]{2}\.(?:[a-z]{2,24}(?![a-z0-9])|(?:한국|닷컴|닷넷)(?![\p{L}\p{N}]))|\d{1,3}(?:\.\d{1,3}){3}/iu;
// 점 앞이 한 글자면 이니셜(J.Kim)로 보고 통과 — 점 뒤가 전부 소문자·전부 대문자면(t.me·X.AI) 주소로 본다.
const SHORT_URL_LIKE = /(?<![\p{L}\p{N}-])[\p{L}\p{N}]\.(?:[a-z]{2,24}|[A-Z]{2,24})(?![A-Za-z0-9])/u;
// 메일 인사말에 넣을 이름의 최대 길이(원문 기준) — 넘으면 이름이 아니라고 보고 인사말 없이 보낸다(가입 이름은 40자 상한이 먼저 막는다).
const MAIL_NAME_MAX = 200;

export const stripInvisible = (value: string): string => value.replace(INVISIBLE_ALL, "");

// 저장할 이름 — 보이지 않는 글자를 지우고 · 공백 같은 글자는 공백으로 · 홀로 쓴 이체자 선택자는 지우고 · 이어 쓴 ZWJ·ZWNJ 는 하나로 접고 자리가 맞지 않으면 지우고 · 연속 공백을 하나로 접어 trim.
export const cleanName = (value: string): string =>
  stripInvisible(value)
    .replace(BLANK_LIKE_ALL, " ")
    .replace(STRAY_VS_ALL, "")
    .replace(JOINER_RUN_ALL, foldJoinerRun)
    .replace(JOINER_ALL, (joiner, kept?: string) => (kept === undefined ? "" : joiner))
    .replace(/ {2,}/g, " ")
    .trim();

// 제어문자를 공백으로 접는다(실제 지역 깃발은 살린다) — 메일 인사말·옛 org.name 상호 대체값이 같이 쓴다.
export const controlCharsToSpace = (value: string): string => value.replace(FLAG_OR_CONTROL_ALL, (_match, flag?: string) => flag ?? " ");

export const hasControlChars = (value: string): boolean => [...value.matchAll(FLAG_OR_CONTROL_ALL)].some(([, flag]) => !flag);

export const hasVisibleChars = (value: string): boolean => value.replace(BLANK_ALL, "") !== "";

// 전각(．：／ｈ…)은 NFKC 로 · U+3002(마침표)는 「.」로 바꾸고 보이지 않는 글자·결합 부호를 지운 뒤 본다 — 메일 클라이언트가 링크로 만드는 우회를 막는다(공백 끼운 꼴은 링크가 안 돼 그대로 둔다).
export const looksLikeUrl = (value: string): boolean => {
  const probe = value.normalize("NFKC").replace(/\u3002/g, ".").replace(IGNORABLE_ALL, "");
  return URL_LIKE.test(probe) || SHORT_URL_LIKE.test(probe);
};

// 메일 본문에 넣을 이름 — 제어문자는 공백으로 접어 한 줄로 · 너무 길거나 주소 꼴이거나 보이는 글자가 없으면 빈 문자열(인사말 없이 보낸다).
export const mailSafeName = (value: string): string => {
  if (value.length > MAIL_NAME_MAX) return "";
  const name = cleanName(controlCharsToSpace(stripInvisible(value))).replace(/\s+/g, " ");
  return looksLikeUrl(name) || !hasVisibleChars(name) ? "" : name;
};
